import { useState } from 'react'
import { Button } from '~/components/ui/button'
import { SurveyPanel } from '~/components/assessment/survey-panel'
import { SurveyProgressPanel } from '~/components/assessment/survey-progress-panel'
import { QuickSurveySheet } from '~/components/assessment/quick-survey-sheet'
import { cn } from '~/lib/utils'
import { formatDateTime } from '~/lib/date-utils'
import type {
    AssessmentResponse,
    ChecklistItem,
    SurveyAnswer,
    SurveyTurn,
} from '~/api/types'
import type { JumpTarget } from './types'

/** collecting 상태: 좌측 대화형 설문 + 우측 진행/현재 문항. */
export function CollectingView({
    assessment,
    assessmentId,
    turn,
    checklist,
    answers,
    onTurn,
    onAnswersSaved,
    onScore,
    onDelete,
    deleting,
    scoring,
    jumpTarget,
    onSelectItem,
}: {
    assessment: AssessmentResponse
    assessmentId: string
    turn: SurveyTurn | null
    checklist: ChecklistItem[] | null
    answers: SurveyAnswer[]
    onTurn(turn: SurveyTurn): void
    onAnswersSaved(): void
    onScore(): void
    onDelete(): void
    deleting: boolean
    scoring: boolean
    jumpTarget: JumpTarget | null
    onSelectItem(itemId: string): void
}) {
    // AI 설문(대화형) ↔ 빠른 설문지(폼) 토글. 기본은 AI 설문.
    const [mode, setMode] = useState<'ai' | 'sheet'>('ai')
    const answeredItems = checklist
        ? checklist.filter((item) =>
              item.fields.length > 0
                  ? item.fields.some((field) =>
                        answers.some(
                            (answer) => answer.fieldId === field.fieldId
                        )
                    )
                  : answers.some(
                        (answer) => answer.fieldId === `item:${item.id}`
                    )
          ).length
        : 0

    const modeToggle = (
        <div
            className='border-border bg-card inline-flex shrink-0 rounded-md border p-0.5'
            role='tablist'
            aria-label='설문 모드'>
            {(
                [
                    ['ai', 'AI 설문'],
                    ['sheet', '빠른 설문지'],
                ] as const
            ).map(([value, label]) => (
                <button
                    key={value}
                    type='button'
                    role='tab'
                    aria-selected={mode === value}
                    onClick={() => setMode(value)}
                    className={cn(
                        'rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                        mode === value
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                    )}>
                    {label}
                </button>
            ))}
        </div>
    )

    if (mode === 'sheet') {
        return (
            <div className='flex h-full min-h-0 flex-col'>
                <div className='mb-3 flex shrink-0 items-center justify-between gap-2'>
                    {modeToggle}
                    <Button
                        variant='destructive'
                        size='sm'
                        onClick={onDelete}
                        disabled={deleting}>
                        {deleting ? '삭제 중…' : '평가 삭제'}
                    </Button>
                </div>
                <div className='min-h-0 flex-1'>
                    <QuickSurveySheet
                        assessmentId={assessmentId}
                        checklist={checklist}
                        answers={answers}
                        onSaved={onAnswersSaved}
                        onScore={onScore}
                        scoring={scoring}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className='flex h-full min-h-0 flex-col gap-3'>
            <div className='shrink-0'>{modeToggle}</div>
            <div className='grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-4 lg:grid-cols-[280px_minmax(0,1fr)_400px] lg:grid-rows-[minmax(0,1fr)]'>
                <aside className='border-border bg-card hidden min-h-0 flex-col rounded-md border p-4 lg:flex'>
                    <h2 className='text-foreground text-sm font-semibold'>
                        평가 정보
                    </h2>
                    <dl className='mt-3 grid gap-2 text-sm'>
                        <div>
                            <dt className='text-muted-foreground text-xs'>
                                시작일
                            </dt>
                            <dd className='text-foreground mt-0.5 font-medium'>
                                {formatDateTime(assessment.createdAt)}
                            </dd>
                        </div>
                        <div>
                            <dt className='text-muted-foreground text-xs'>
                                최근 진행
                            </dt>
                            <dd className='text-foreground mt-0.5 font-medium'>
                                {formatDateTime(assessment.updatedAt)}
                            </dd>
                        </div>
                        <div>
                            <dt className='text-muted-foreground text-xs'>
                                답변 현황
                            </dt>
                            <dd className='text-foreground mt-0.5 font-medium'>
                                {answeredItems}/{checklist?.length ?? 0}개 항목
                            </dd>
                        </div>
                    </dl>
                    <div className='border-border mt-4 border-t pt-4'>
                        <Button
                            variant='destructive'
                            size='sm'
                            className='w-full'
                            onClick={onDelete}
                            disabled={deleting}>
                            {deleting ? '삭제 중…' : '평가 삭제'}
                        </Button>
                    </div>
                </aside>

                <div className='flex h-full min-h-0 min-w-0 flex-col'>
                    <h2 className='text-foreground mb-2 text-sm font-semibold'>
                        AI 설문
                    </h2>
                    <SurveyPanel
                        key={assessmentId}
                        assessmentId={assessmentId}
                        onTurn={onTurn}
                        onScore={onScore}
                        scoring={scoring}
                        jumpTarget={jumpTarget}
                        initialChecklist={checklist}
                        initialAnswers={answers}
                        seedInitialSurvey={Boolean(assessment.industry)}
                    />
                </div>

                {/* 우측: 진행도 + 현재 문항 + 전체 문항·답변 목록 */}
                <SurveyProgressPanel
                    answered={turn?.progressAnswered ?? 0}
                    total={turn?.progressTotal ?? 0}
                    currentItem={turn?.currentItem}
                    currentField={turn?.currentField}
                    checklist={checklist}
                    answers={answers}
                    done={turn?.done}
                    onSelectItem={onSelectItem}
                />
            </div>
        </div>
    )
}
